import React, { useEffect, useState } from 'react';
import { Search, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface GlobalSearchProps {
  onAddComplete?: () => void;
}
export function GlobalSearch({
  onAddComplete
}: GlobalSearchProps) {
  const [searchValue, setSearchValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalStage, setModalStage] = useState<'connecting' | 'matched' | 'layering' | 'ready'>('connecting');
  const [foundItems, setFoundItems] = useState<number>(0);
  const handleFocus = () => {
    setIsFocused(true);
    if (!searchValue) {
      // Auto-type "Dunder Mifflin"
      const targetText = 'Dunder Mifflin';
      let currentIndex = 0;
      const typingInterval = setInterval(() => {
        if (currentIndex <= targetText.length) {
          setSearchValue(targetText.slice(0, currentIndex));
          currentIndex++;
        } else {
          clearInterval(typingInterval);
          setShowDropdown(true);
        }
      }, 80);
    }
  };
  const handleAddClick = () => {
    setShowDropdown(false);
    setShowModal(true);
    setModalStage('connecting');
    setFoundItems(0);
    // After 1.5 seconds, show entity matched
    setTimeout(() => {
      setModalStage('matched');
      // After 1.5 seconds, show layering additional sources
      setTimeout(() => {
        setModalStage('layering');
        // Cascade through finding each item
        const dataItems = 4;
        for (let i = 1; i <= dataItems; i++) {
          setTimeout(() => {
            setFoundItems(i);
          }, i * 400);
        }
        // After all items are found + brief pause, show ready
        setTimeout(() => {
          setModalStage('ready');
          // After 1.5 seconds, close modal and complete
          setTimeout(() => {
            setShowModal(false);
            setSearchValue('');
            setIsFocused(false);
            if (onAddComplete) onAddComplete();
          }, 1500);
        }, dataItems * 400 + 800);
      }, 1500);
    }, 1500);
  };
  const handleClear = () => {
    setSearchValue('');
    setShowDropdown(false);
  };
  return <>
      <div className="relative flex-1 max-w-2xl mx-8" data-id="element-1517">
        <div className="relative" data-id="element-1518">
          <input type="text" value={searchValue} onChange={e => setSearchValue(e.target.value)} onFocus={handleFocus} onBlur={() => {
          setTimeout(() => {
            setIsFocused(false);
            setShowDropdown(false);
          }, 200);
        }} placeholder="Search Companies" className="w-full px-4 py-2 pl-4 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" data-id="element-1519" />
          {searchValue ? <button onClick={handleClear} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-1520">
              <X className="w-5 h-5" data-id="element-1521" />
            </button> : <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1522" />}
        </div>

        {/* Dropdown Results */}
        <AnimatePresence data-id="element-1523">
          {showDropdown && <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="absolute top-full mt-2 w-full bg-white rounded-lg border border-gray-200 shadow-lg z-50 overflow-hidden" data-id="element-1524">
              <div className="p-2 border-b border-gray-100 text-xs text-gray-500" data-id="element-1525">
                Showing 0 of 0 existing accounts
              </div>

              {/* New Result */}
              <motion.div initial={{
            backgroundColor: 'rgb(255, 255, 255)'
          }} animate={{
            backgroundColor: ['rgb(255, 255, 255)', 'rgb(238, 242, 255)', 'rgb(255, 255, 255)']
          }} transition={{
            duration: 2,
            repeat: Infinity
          }} className="p-4 border-b border-gray-100" data-id="element-1526">
                <div className="flex items-center justify-between" data-id="element-1527">
                  <div data-id="element-1528">
                    <h4 className="font-semibold text-gray-900 mb-1" data-id="element-1529">
                      DUNDER MIFFLIN PAPER COMPANY
                    </h4>
                    <p className="text-sm text-gray-600 mb-2" data-id="element-1530">
                      1725 SLOUGH AVENUE, SCRANTON, PA, 18505
                    </p>
                    <div className="flex items-center gap-2" data-id="element-1531">
                      <span className="px-2 py-0.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-full" data-id="element-1532">
                        Active
                      </span>
                      <span className="px-2 py-0.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-full" data-id="element-1533">
                        HQ
                      </span>
                    </div>
                  </div>
                  <motion.button whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} onClick={handleAddClick} className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg transition-colors" data-id="element-1534">
                    ADD
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Data Discovery Modal */}
      <AnimatePresence data-id="element-1535">
        {showModal && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/50 z-50" data-id="element-1536" />

            {/* Modal */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1537">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-8" data-id="element-1538">
                {modalStage === 'connecting' && <div className="text-center" data-id="element-1539">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 1,
                repeat: Infinity,
                ease: 'linear'
              }} className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full mx-auto mb-6" data-id="element-1540" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-1541">
                      Connecting to your preferred bureau
                    </h3>
                    <p className="text-gray-600" data-id="element-1542">Searching for company...</p>
                  </div>}

                {modalStage === 'matched' && <div className="text-center" data-id="element-1543">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1544">
                      <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1545">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-1546" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-1547">
                      Entity Matched
                    </h3>
                    <p className="text-gray-600" data-id="element-1548">
                      Dunder Mifflin Paper Company verified
                    </p>
                  </div>}

                {modalStage === 'layering' && <div className="text-center" data-id="element-1549">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 1,
                repeat: Infinity,
                ease: 'linear'
              }} className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full mx-auto mb-6" data-id="element-1550" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-1551">
                      Layering additional data sources
                    </h3>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-1552">
                      Enriching bureau data with:
                    </p>
                    <div className="space-y-3 text-left" data-id="element-1553">
                      {['People', 'Private Financials', 'Recent News', 'Social & Web Presence'].map((item, index) => <motion.div key={item} initial={{
                  opacity: 0,
                  x: -20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: index * 0.2
                }} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-id="element-1554">
                          {foundItems > index ? <motion.div initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} transition={{
                    type: 'spring',
                    stiffness: 500,
                    damping: 30
                  }} className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-1555">
                              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-1556">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-1557" />
                              </svg>
                            </motion.div> : <div className="w-2 h-2 bg-indigo-600 rounded-full" data-id="element-1558" />}
                          <span className={`font-medium transition-colors ${foundItems > index ? 'text-gray-900' : 'text-gray-600'}`} data-id="element-1559">
                            {item}
                          </span>
                        </motion.div>)}
                    </div>
                  </div>}

                {modalStage === 'ready' && <div className="text-center" data-id="element-1560">
                    <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1561">
                      <svg className="w-8 h-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1562">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-1563" />
                      </svg>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1564">
                      Company Added
                    </h3>
                    <p className="text-gray-600" data-id="element-1565">Ready to view...</p>
                  </div>}
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}